import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { _, TranslateDirective, TranslateService } from '@ngx-translate/core';
import { Select } from 'primeng/select';
import { Button } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { LeakDetectionEventsAssigneeService } from '@app/main/leak-detection/services/leak-detection-events-assignee/leak-detection-events-assignee.service';
import { DialogEventTabs } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-tabs.enum';
import { DialogEventInvestigationResultComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-investigation-result/dialog-event-investigation-result.component';
import { DialogEventLocationCardComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-location-card/dialog-event-location-card.component';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventHistory } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-history.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';
import { DIALOG_EVENT_OVERLAY_OPTIONS } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-overlay.config';
import { needsAssignee } from '@app/main/leak-detection/dialogs/dialog-event/utils/dialog-event-draft';

@Component({
  selector: 'gc-dialog-event-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateDirective,
    Button,
    Select,
    SvgIconComponent,
    DialogEventInvestigationResultComponent,
    DialogEventLocationCardComponent,
  ],
  templateUrl: './dialog-event-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class DialogEventDetailsComponent {
  protected readonly dialogState = inject(DialogEventState);
  protected readonly history = inject(DialogEventHistory);
  private readonly tabs = inject(DialogEventTabsService);

  private readonly assigneeService = inject(LeakDetectionEventsAssigneeService);
  private readonly translateService = inject(TranslateService);

  protected readonly overlayOptions = DIALOG_EVENT_OVERLAY_OPTIONS;

  protected readonly form = this.dialogState.form;

  protected readonly draft = this.dialogState.draft;

  private readonly assignableUsers = toSignal(this.assigneeService.assignableUsers$, {
    initialValue: [] as string[],
  });

  protected readonly assigneeOptions = computed(() => {
    const users = this.assignableUsers().map(user => ({ label: user, value: user }));

    const canBeUnassigned = !needsAssignee(this.dialogState.status()) || !this.draft().assignedTo;

    return canBeUnassigned
      ? [{ label: this.translateService.instant(_(`@@shared-events-not-assigned__text`)), value: null }, ...users]
      : users;
  });

  protected goToComments(): void {
    this.history.showUserCommentsOnly();
    this.tabs.activeTab.set(DialogEventTabs.History);
  }
}
